import { RENTLE_SHOP_TERMS_VERSION } from 'common/constants/urls';
import { Callable } from 'common/frontend/callable';
import { newSignupEvent } from 'common/services/analytics';
import { CurrencyObject, Languages, ProductTypes } from 'common/types';
import { isRentleEmail } from 'common/utils/common';
import {
	getCurrencyObjectFromCurrencyCode,
	getDefaultCurrencyObject,
} from 'common/utils/currencyUtils';
import { getDateFormatFromCountry } from 'common/utils/dateUtils';
import moment from 'moment';
import React, { useState, createContext } from 'react';
import currencyList from 'common/services/currencyCodes.json';
import { setGaUserProperties } from 'common/services/analytics';
import { push, Routes } from 'routing';
import { sleep } from 'common/utils/async';
import {
	CountryCode,
	getInitialUnitSystemFromCountry,
	getTaxExcludedFromCountry,
} from 'common/modules/atoms/countries';
import { RENTLE_FREE_LANGUAGES } from 'common/locales/utils/constants';
import { getLanguage } from 'services/localization/utils';

interface HandleRegisterParams {
	uid: string;
	email: string;
	shopName: string;
	countryCode: CountryCode;
	affiliateId: string | undefined;
}

interface ContextInterface {
	shopId: string;
	setShopId: (id: string) => void;
	shopAnalyticsDocId: string;
	setShopAnalyticsDocId: (id: string) => void;
	email: string;
	setEmail: (email: string) => void;
	handleRegister: (args: HandleRegisterParams) => Promise<void>;
	registrationStatus: 'initial' | 'registering' | 'done' | 'error';
	setRegistrationStatus: (value: 'initial' | 'registering' | 'done' | 'error') => void;
}

const INITIAL_STATE: ContextInterface = {
	shopId: '',
	setShopId: () => {},
	shopAnalyticsDocId: '',
	setShopAnalyticsDocId: () => {},
	email: '',
	setEmail: () => {},
	handleRegister: async () => {},
	registrationStatus: 'initial',
	setRegistrationStatus: () => {},
};

export const RegisterContext = createContext<ContextInterface>(INITIAL_STATE);

export const RegisterContextProvider = (props: any) => {
	const { children }: { children: any } = props;
	const [shopId, setShopId] = useState<string>(INITIAL_STATE.shopId);
	const [shopAnalyticsDocId, setShopAnalyticsDocId] = useState<string>(
		INITIAL_STATE.shopAnalyticsDocId,
	);
	const [email, setEmail] = useState<string>(INITIAL_STATE.email);
	const [registrationStatus, setRegistrationStatus] = useState<
		ContextInterface['registrationStatus']
	>(INITIAL_STATE.registrationStatus);

	const handleRegister = async (args: HandleRegisterParams) => {
		const { uid, email, shopName, countryCode, affiliateId } = args;
		setRegistrationStatus('registering');
		push(Routes.REGISTER_GET_STARTED);
		try {
			const [{ shopId, shopAnalyticsDocId }] = await Promise.all([
				Callable.users.registerShop({
					shopType: ProductTypes.RENTAL,
					uid,
					email,
					termsVersionAccepted: RENTLE_SHOP_TERMS_VERSION,
					shopInfo: {
						name: shopName,
						defaultLanguage: getDefaultLanguage(getLanguage()),
						country: countryCode,
						currency: getInitialCurrencyObjectFromCountry(countryCode),
						dateFormat: getDateFormatFromCountry(countryCode),
						unitSystem: getInitialUnitSystemFromCountry(countryCode),
						taxExcluded: getTaxExcludedFromCountry(countryCode),
					},
					timeZone: moment.tz.guess(),
					affiliateId,
				}),
				sleep(3000),
			]);
			setShopId(shopId);
			setShopAnalyticsDocId(shopAnalyticsDocId);
			setEmail(email);
			setGaUserProperties(
				{
					internal_user: isRentleEmail(email),
				},
				{ sendToAllTags: true },
			);
			newSignupEvent({
				method: 'email',
				internal_user: isRentleEmail(email),
				affiliate_id: affiliateId,
				email,
				merchant_id: shopId,
			});
			setRegistrationStatus('done');
		} catch (err) {
			setRegistrationStatus('error');
		}
	};

	return (
		<RegisterContext.Provider
			value={{
				shopId,
				setShopId,
				shopAnalyticsDocId,
				setShopAnalyticsDocId,
				email,
				setEmail,
				handleRegister,
				registrationStatus,
				setRegistrationStatus,
			}}
		>
			{children}
		</RegisterContext.Provider>
	);
};

const getInitialCurrencyObjectFromCountry = (countryCode: string): CurrencyObject => {
	const currencyValues = Object.values(currencyList.currencies);

	const filteredCurrency = currencyValues.filter((value) =>
		value.countries.some((country) => country === countryCode),
	)[0];

	return getCurrencyObjectFromCurrencyCode(filteredCurrency.code) ?? getDefaultCurrencyObject();
};

const getDefaultLanguage = (language: Languages | null) => {
	return language && RENTLE_FREE_LANGUAGES.includes(language) ? language : 'en';
};
